import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useAppSelector } from '@store/hooks';
import { selectFloorLabelById } from '@store/selectors/monitor.floors.selectors';
import { selectProject } from '@store/selectors/monitor.selectors';
import type { IProject } from '@d-orca/shared/trusstor';
import type { Anchor } from '../MainView/buildingViewAnchors';
import type { IMonitorReplay, IReplayEvent } from '@interfaces/IMonitorApi';
import { EVENT_KINDS, kindSpecOf, type IEventCopyContext } from './markers';
import styles from './replayEventLayer.module.css';

interface IReplayEventLayerProps {
  /** The building's floor rows (`floor<id>` markers). */
  anchors: Anchor[];
  events: readonly IReplayEvent[];
  equipment: IMonitorReplay['equipment'];
}

/** Room between the building's right edge and a toast. */
const TOAST_GAP = 14;

/**
 * Events over the building, the replay's crossed ones or the live ones: each event's floor rows pulse
 * three times in its marker colour (not the peak's), and a toast right of the
 * building, level with the event's highest floor (the building's top floor when
 * it has none), reads `HH:mm · floors` / title / sub line and fades out.
 */
export function ReplayEventLayer({ anchors, events, equipment }: IReplayEventLayerProps) {
  const { t } = useTranslation();
  const floor = useAppSelector(selectFloorLabelById);
  // Replay exists only on the Monitor, after its snapshot has loaded the project.
  const { tz } = useAppSelector(selectProject) as IProject;
  if (anchors.length === 0) return null;
  const copyContext: IEventCopyContext = { t, equipment, floorLabel: floor };
  const right = Math.max(...anchors.map((row) => row.left + row.width)) + TOAST_GAP;
  const top = (rows: Anchor[]) => rows.reduce((highest, row) => (row.top < highest.top ? row : highest));

  return (
    <div className={styles.layer}>
      {events.map((event) => {
        const key = JSON.stringify(event);
        const spec = kindSpecOf(event);
        const ids = spec.floorIds(event);
        const rows = anchors.filter((row) => ids.includes(row.floorId));
        const anchor = top(rows.length > 0 ? rows : anchors);
        const color = { '--c': EVENT_KINDS[event.kind].color } as CSSProperties;
        const [title, sub] = spec.toast(event, copyContext);
        const head = [DateTime.fromISO(event.at, { zone: tz }).toFormat('HH:mm'), ...(rows.length > 0 ? [rows.map((row) => floor(row.floorId)).join(', ')] : [])];
        return (
          <div key={key}>
            {rows.map((row) => (
              <div
                key={row.floorId}
                className={styles.pulse}
                data-testid="replay-pulse"
                style={{ ...color, left: row.left, top: row.top, width: row.width, height: row.height }}
              />
            ))}
            <div role="status" className={styles.toast} style={{ ...color, left: right, top: anchor.top + anchor.height / 2 }}>
              <i>{head.join(' · ')}</i>
              <b>{title}</b>
              <span>{sub}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
